import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IPowerLine, IScatters, IWinningLine } from '@GFTypes/GameStoreTypes';
import { IActionResponseStep } from '@GFTypes/Responses/ActionResponse';
import { IStepExtension } from '../Responses/SpinResponseProcessor';
import { normalizeScatterPositions, normalizeWaysPositions } from '../Utils/SymbolIndexNormalizerUtils';
import { buildSelectionMaskFromPositions } from '../Utils/Utils';
import { getMultiplierForMessage, getMultiplierForScatterMessage } from './WinMessageMultiplierDataProcessor';

export const processWinningLines = (step: IActionResponseStep, stepExtension: IStepExtension): IWinningLine[] | undefined => {
    if (!step.winningLines) return;

    const winlines = step.winningLines?.map((winline) => {
        const { id, symbolsCount } = winline;
        const paylines = gameInstance.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).payLines;
        const payline = paylines[id];
        const positions = getPositionsFromWinningLine(payline, symbolsCount, winline.direction);
        const symbolIds = getSymbolIdsFromPositions(positions, stepExtension.visibleSymbols);
        const symbolId = getWinningLineSymbolId(symbolIds);
        const onlyInterchangeableSymbols = hasOnlyInterchangeableSymbols(symbolIds);
        const selectionMask = buildSelectionMaskFromPositions(positions, stepExtension.visibleSymbols);
        const winMessageMultiplier = getMultiplierForMessage(step, true, winline.multiplier, symbolIds);

        return {
            ...winline,
            positions,
            symbolId,
            onlyInterchangeableSymbols,
            selectionMask,
            winMessageMultiplier
        };
    });

    return winlines;
};

export const processWays = (step: IActionResponseStep, stepExtension: IStepExtension): IPowerLine[] | undefined => {
    if (!step.powerLines) return;

    const powerLines = step.powerLines?.map((powerLine) => {
        const positions = normalizeWaysPositions(powerLine.positions);
        const symbolIds = getSymbolIdsFromPositions(positions, stepExtension.visibleSymbols);
        const onlyInterchangeableSymbols = hasOnlyInterchangeableSymbols(symbolIds);
        const selectionMask = buildSelectionMaskFromPositions(positions, stepExtension.visibleSymbols);
        const winMessageMultiplier = getMultiplierForMessage(step, false, powerLine.multiplier, symbolIds);

        return {
            ...powerLine,
            positions,
            onlyInterchangeableSymbols,
            selectionMask,
            winMessageMultiplier
        };
    });

    return powerLines;
};

export const processScatters = (step: IActionResponseStep, stepExtension: IStepExtension): IScatters[] | undefined => {
    if (!step.scatters) return;

    const scatters = step.scatters?.map((scatter) => {
        const positions = normalizeScatterPositions(scatter.positions, stepExtension.visibleSymbols[0].length);
        const selectionMask = buildSelectionMaskFromPositions(positions, stepExtension.visibleSymbols);
        const winMessageMultiplier = getMultiplierForScatterMessage(step);

        return {
            ...scatter,
            positions,
            selectionMask,
            winMessageMultiplier
        };
    });

    return scatters;
};

export const getPositionsFromWinningLine = (winningLine: number[], symbolsCount: number, direction: number): [number, number][] => {
    const startIndex = direction === 1 ? 0 : -symbolsCount;
    const endIndex = direction === 1 ? symbolsCount : winningLine.length;
    return winningLine.map((symbolIndex, reelIndex) => [reelIndex, symbolIndex] as [number, number]).slice(startIndex, endIndex);
};

export const getPositionsFromWinningLineByWinningReels = (winningLine: number[], winningReels: number[]): [number, number][] => {
    return winningLine.map((symbolIndex, reelIndex) => [reelIndex, symbolIndex] as [number, number]).filter(([reel]) => winningReels.includes(reel));
};

export const getWinningLineSymbolId = (symbolIds: number[]): number => {
    const onlyWildSymbols = hasOnlyWildSymbols(symbolIds);
    const onlyInterchangeableSymbols = hasOnlyInterchangeableSymbols(symbolIds);
    const firstNormalSymbolId = getFirstNormalSymbolId(symbolIds);
    const symbolId = onlyInterchangeableSymbols || onlyWildSymbols ? symbolIds[0] : firstNormalSymbolId;
    return symbolId ?? -1;
};

const getSymbolIdsFromPositions = (positions: [number, number][], visibleSymbols: number[][]): number[] => {
    return positions.map(([reel, symbol]) => visibleSymbols[reel][symbol]);
};

const hasOnlyWildSymbols = (symbols: number[]): boolean => {
    const { wild } = gameInstance.gameStore.get(EGameStoreKeys.Features);
    return symbols.every((id) => wild?.includes(id));
};

const hasOnlyInterchangeableSymbols = (symbols: number[]): boolean => {
    const { interchangeableSymbol } = gameInstance.gameStore.get(EGameStoreKeys.Features);
    return symbols.every((id) => interchangeableSymbol?.includes(id));
};

const getFirstNormalSymbolId = (symbols: number[]): number | undefined => {
    const { wild } = gameInstance.gameStore.get(EGameStoreKeys.Features);
    const { interchangeableSymbol } = gameInstance.gameStore.get(EGameStoreKeys.Features);

    return symbols.find((id) => !wild?.includes(id) && !interchangeableSymbol?.includes(id));
};
